import React, { useEffect, useRef, Suspense } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/layouts/main";
import { apiPost } from "../api/apiREST";
import { useQueryClient } from "@tanstack/react-query";
import { performLogout } from "../utils/logoutUtils";
import { registerFCMTokenForUser } from "../utils/fcmRegistration";
import Loading from "../components/core/Loading";
import Products from "../pages/products";

const Profile = React.lazy(() => import("../pages/profile"));
const Token = React.lazy(() => import("../pages/token/index"));
const Auth = React.lazy(() => import("../pages/auth"));
const ResetPasswordConfirm = React.lazy(
  () => import("../pages/auth/reset-password-confirm")
);
const Wordpress = React.lazy(() => import("../pages/wordpress"));
const Packages = React.lazy(() => import("../pages/packages"));
const Support = React.lazy(() => import("../pages/support"));
const Assistant = React.lazy(() => import("../pages/assistant"));
const TicketChat = React.lazy(() => import("../pages/support/TicketChat"));
const Notifications = React.lazy(() => import("../pages/notifications/index"));
const Wallet = React.lazy(() => import("../pages/wallet"));
const NotFound = React.lazy(() => import("../pages/not-found"));

export default function Routers() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  const queryClient = useQueryClient();

  const isPublicRoute = (path: string) => {
    return (
      path === "/login" ||
      path === "/auth/reset-password-confirm" ||
      path.startsWith("/auth/reset-password-confirm/")
    );
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem("refresh_token");

      if (!refresh_token) {
        if (!isPublicRoute(location.pathname)) {
          performLogout(queryClient, navigate);
        }
        return;
      }

      const response = await apiPost("auth/jwt/refresh/", {
        refresh: refresh_token,
      });

      if (response.error) {
        // If refresh fails, use comprehensive logout
        performLogout(queryClient, navigate);
        return;
      }

      // Update tokens if refresh was successful
      if (response.data?.access) {
        localStorage.setItem("access_token", response.data.access);
      }
      if (response.data?.refresh) {
        localStorage.setItem("refresh_token", response.data.refresh);
      }

      // Register FCM token after successful token refresh (non-blocking)
      console.log("🔄 Token refreshed successfully, registering FCM...");
      registerFCMTokenForUser().catch((error) => {
        console.error("❌ FCM registration failed after token refresh:", error);
      });
    } catch (error) {
      console.error("Refresh token error:", error);
      performLogout(queryClient, navigate);
    }
  };

  useEffect(() => {
    // Only run refresh token on initial load, not on navigation
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      refreshToken();
    }
  }, []);

  return (
    <Suspense fallback={<Loading size="fullscreen" />}>
      <Routes location={location} key={location.key}>
        <Route index path="/login" element={<Auth />} />
        <Route
          path="/auth/reset-password-confirm"
          element={<ResetPasswordConfirm />}
        />
        <Route
          path="/auth/reset-password-confirm/:uid/:token"
          element={<ResetPasswordConfirm />}
        />

        <Route path="/token" element={<Layout location="/token" />}>
          <Route index path="/token" element={<Token />} />
        </Route>

        <Route path="/packages" element={<Layout location="/packages" />}>
          <Route index path="/packages" element={<Packages />} />
        </Route>

        <Route path="/" element={<Layout location="/" />}>
          <Route index path="/" element={<Wordpress />} />
        </Route>

        <Route path="/profile" element={<Layout location="/profile" />}>
          <Route index path="/profile" element={<Profile />} />
        </Route>

        <Route path="/support" element={<Layout location="/support" />}>
          <Route index path="/support" element={<Support />} />
        </Route>

        <Route path="/wallet" element={<Layout location="/wallet" />}>
          <Route index path="/wallet" element={<Wallet />} />
        </Route>

        <Route path="/assistant" element={<Layout location="/assistant" />}>
          <Route index path="/assistant" element={<Assistant />} />
        </Route>

        <Route path="/products" element={<Layout location="/products" />}>
          <Route index path="/products" element={<Products />} />
        </Route>

        <Route
          path="/support/tickets/:ticketId"
          element={<Layout location="/support" />}
        >
          <Route
            index
            path="/support/tickets/:ticketId"
            element={<TicketChat />}
          />
        </Route>

        {/* <Route
          path="/notifications"
          element={<Layout location="/notifications" />}
        >
          <Route index path="/notifications" element={<Notifications />} />
        </Route> */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
